/**
 * mergeDuplicateProducts — Dédup + agrégation des tailles
 *
 * Certains partenaires (notamment iQueens) renvoient le même article
 * en plusieurs entrées du catalogue, une par taille (ex: "Adidas T-Shirt
 * Femme XL", "Adidas T-Shirt Femme S"). Plutôt que de simplement supprimer
 * les doublons, on les FUSIONNE en un seul produit dont :
 *   - le nom est nettoyé (sans la taille en suffixe — déjà fait à la source)
 *   - le champ `sizes` agrège les tailles disponibles de tous les doublons
 *
 * Résultat : 1 produit affiché dans le catalogue, avec la liste complète
 * des tailles dispo dans la vue détail.
 */
import { normalizedNameKey, stripTrailingSize, extractTrailingSize } from './productNameUtils';

/**
 * Fusionne les doublons d'une liste de produits.
 *
 * Stratégie en 2 passes pour gérer les cas réels :
 *   Pass 1 (stricte) : nom_normalisé + brand + prix arrondi → fusion certaine
 *   Pass 2 (tolérante) : nom_normalisé + brand → fusion si même article à
 *           des prix légèrement différents (ex: même article réimporté avec
 *           un prix mis à jour → 2 lignes en DB)
 *
 * On ne fusionne PAS sur juste le nom : 2 marques peuvent avoir un produit
 * de même nom (ex: "T-shirt blanc" chez H&M et chez Zara).
 *
 * @param {Array} products
 * @param {object} [opts]
 * @param {boolean} [opts.debug] - Si true, log les doublons potentiels non fusionnés
 * @param {boolean} [opts.tolerant=true] - Active la pass 2 tolérante (par défaut true)
 * @returns {Array} produits dédupliqués avec `sizes` agrégées
 */
export function mergeDuplicateProducts(products, opts = {}) {
  if (!Array.isArray(products) || products.length === 0) return products || [];
  const tolerant = opts.tolerant !== false; // true par défaut

  // v1.12.887 — Image incluse dans la clé : 2 produits avec brand+name+price
  // identiques mais images différentes = modèles distincts (cas Tamaris :
  // noms génériques comme "Bottine noir" partagés entre 41 modèles distincts).
  // Les vraies variantes de tailles d'un même article partagent la MÊME image.
  const imgKey = (p) => String(p?.image || p?.image_url || '').slice(0, 200);

  // Clé stricte : brand + nom_normalisé + prix arrondi + image
  const strictKey = (p) => {
    const name  = normalizedNameKey(p?.name || '');
    const brand = String(p?.brand || p?.partner || '').trim().toLowerCase();
    const price = Math.round(Number(p?.price) || 0);
    return `${brand}|${name}|${price}|${imgKey(p)}`;
  };

  // Clé tolérante : brand + nom_normalisé + image (sans prix)
  // Permet de fusionner les variantes de tailles même si elles ont des prix
  // légèrement différents (-1€/+1€). Sécuriser : on plafonne l'écart de prix
  // à 30% pour éviter de fusionner 2 articles vraiment distincts.
  const looseKeyForGroup = (p) => {
    const name  = normalizedNameKey(p?.name || '');
    const brand = String(p?.brand || p?.partner || '').trim().toLowerCase();
    return `${brand}|${name}|${imgKey(p)}`;
  };

  // Clé "ultra loose" pour debug : juste le nom_normalisé
  const veryLooseKey = (p) => normalizedNameKey(p?.name || '');

  const groups = new Map(); // key → { product, sizesSet, sizesFromNameSet, count, refPrice }
  const looseGroups = new Map(); // looseKey → strictKey du premier vu (pour redirection)
  const seenLoose = new Map(); // veryLooseKey → liste pour debug

  for (const p of products) {
    if (!p) continue;
    const key = strictKey(p);
    const looseK = looseKeyForGroup(p);
    // ═══ Sizes : champ explicite OU extrait du nom ═══
    // Certains partenaires (notamment iQueens) mettent la taille uniquement
    // en suffixe du nom sans renseigner p.sizes. On l'extrait pour pouvoir
    // l'agréger lors de la fusion des variantes.
    // ═══ FIX 2026-04-26 : on distingue 2 ensembles ═══
    // - psizes : tailles fournies par p.sizes (souvent une liste générique
    //   par défaut comme ['XS','S','M','L','XL'] côté Zara/HM/Cashmere).
    // - psizesFromName : taille EXTRAITE du nom du produit. Si le partenaire
    //   a renvoyé "Article XL" et "Article M" en 2 entrées séparées, ces 2
    //   tailles sont les VRAIES tailles disponibles pour l'article.
    let psizes = Array.isArray(p.sizes) ? p.sizes.filter(Boolean) : [];
    const sizeFromName = extractTrailingSize(p?.name || '');
    let psizesFromName = sizeFromName ? [sizeFromName] : [];
    if (psizes.length === 0 && sizeFromName) psizes = [sizeFromName];

    // Mode debug : tracer les noms normalisés
    if (opts.debug) {
      const vlk = veryLooseKey(p);
      if (!seenLoose.has(vlk)) seenLoose.set(vlk, []);
      seenLoose.get(vlk).push({
        brand: String(p?.brand || p?.partner || '').trim().toLowerCase(),
        price: Math.round(Number(p?.price) || 0),
        name: p?.name,
      });
    }

    // ═══ Pass 1 : match strict (même brand + nom + prix arrondi) ═══
    if (groups.has(key)) {
      const g = groups.get(key);
      for (const s of psizes) g.sizesSet.add(String(s));
      for (const s of psizesFromName) g.sizesFromNameSet.add(String(s));
      g.count += 1;
      if (!g.product.image && p.image) g.product = { ...g.product, image: p.image };
      continue;
    }

    // ═══ Pass 2 (tolérante) : même brand + nom mais prix différent ═══
    // On regarde si on a déjà un groupe avec la même clé loose. Si oui ET
    // l'écart de prix est <= 30%, on fusionne. Sinon, nouveau groupe.
    if (tolerant && looseGroups.has(looseK)) {
      const existingKey = looseGroups.get(looseK);
      const g = groups.get(existingKey);
      if (g) {
        const refPrice = g.refPrice;
        const newPrice = Number(p?.price) || 0;
        // Tolérance : 30% d'écart max OU différence absolue ≤ 5€ (pour les petits prix)
        const diff = Math.abs(refPrice - newPrice);
        const tolerance = Math.max(refPrice * 0.3, 5);
        if (diff <= tolerance) {
          // Fusion tolérante
          for (const s of psizes) g.sizesSet.add(String(s));
          for (const s of psizesFromName) g.sizesFromNameSet.add(String(s));
          g.count += 1;
          if (!g.product.image && p.image) g.product = { ...g.product, image: p.image };
          continue;
        }
      }
    }

    // Nouveau groupe
    groups.set(key, {
      product: p,
      sizesSet: new Set(psizes.map(String)),
      sizesFromNameSet: new Set(psizesFromName.map(String)),
      count: 1,
      refPrice: Number(p?.price) || 0,
    });
    if (tolerant && !looseGroups.has(looseK)) {
      looseGroups.set(looseK, key);
    }
  }

  // Mode debug : signaler les cas suspects (même nom normalisé mais brand/prix différents)
  if (opts.debug) {
    let warnCount = 0;
    for (const [lk, entries] of seenLoose.entries()) {
      if (entries.length < 2) continue;
      // Au moins 2 produits avec ce nom normalisé. Vérifier qu'ils sont
      // dans des groupes différents (= pas fusionnés).
      const brands = new Set(entries.map(e => e.brand));
      const prices = new Set(entries.map(e => e.price));
      if (brands.size > 1 || prices.size > 1) {
        warnCount++;
        if (warnCount <= 5) {
          console.log(`[mergeDuplicateProducts] ⚠️ "${lk}" ×${entries.length} — brands: ${[...brands].join(',')} prices: ${[...prices].join(',')}`);
        }
      }
    }
    if (warnCount > 5) console.log(`[mergeDuplicateProducts] … et ${warnCount - 5} autres cas suspects`);
  }

  // Reconstruire la liste avec sizes triées
  // ═══ FIX 2026-04-26 : si plusieurs doublons fusionnés AVEC des tailles ═══
  // ═══ extraites des noms → ces tailles sont les VRAIES tailles disponibles. ═══
  // Sinon (1 seul produit ou aucune taille extraite) → on garde sizesSet
  // (= valeurs par défaut du partenaire, souvent générique XS-XL).
  const out = [];
  for (const { product, sizesSet, sizesFromNameSet, count } of groups.values()) {
    // Décider quelle source de tailles utiliser
    let finalSizes;
    if (count > 1 && sizesFromNameSet.size > 0) {
      // Cas typique : iQueens renvoie "Article S" + "Article M" → on a fusionné
      // 2 doublons et extrait S, M des noms. C'est la vérité de terrain.
      finalSizes = sizesFromNameSet;
    } else {
      finalSizes = sizesSet;
    }
    if (finalSizes.size === 0) {
      out.push(product);
    } else {
      // Tri intelligent : lettres (XS,S,M,L,XL,XXL...) puis nombres croissants
      const arr = [...finalSizes];
      const order = { 'XXS': 1, 'XS': 2, 'S': 3, 'M': 4, 'L': 5, 'XL': 6, 'XXL': 7, 'XXXL': 8, 'UNIQUE': 99 };
      arr.sort((a, b) => {
        const oa = order[a.toUpperCase()];
        const ob = order[b.toUpperCase()];
        if (oa && ob) return oa - ob;             // 2 lettres
        if (oa) return -1;                         // a est lettre, b est nombre → lettre d'abord
        if (ob) return 1;
        // 2 nombres : tri numérique
        const na = parseFloat(a);
        const nb = parseFloat(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return String(a).localeCompare(String(b));
      });
      // ═══ Nettoyer le nom : retirer la taille en suffixe ═══
      // Si le produit s'appelle "Chemise Desigual White XS" et qu'on a fusionné
      // [XS, S, M], on veut afficher "Chemise Desigual White" + boutons [XS, S, M].
      // Sinon l'utilisateur voit "XS" 2 fois (dans le nom et dans les tailles).
      // stripTrailingSize ne touche pas le nom s'il n'y a pas de suffixe taille.
      const cleanName = stripTrailingSize(product?.name || '');
      out.push({ ...product, name: cleanName, sizes: arr });
    }
  }
  return out;
}
